/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {IoMatrixWorkflowComponent} from '../io-matrix-workflow.component';
import {
    buildIoMatrixSnapshotOptions,
    IoMatrixSnapshot,
    IoMatrixView,
    POWERTRAIN_IO_MATRIX_DISPLAY_NAME,
    variantTokenPartsAreValid
} from '../../../model/io-matrix.models';

@Component({
  selector: 'app-io-matrix',
  templateUrl: './io-matrix.component.html',
  styleUrls: ['./io-matrix.component.scss', '../io-matrix-workflow.component.scss'],
  standalone: false
})
export class IoMatrixComponent extends IoMatrixWorkflowComponent {
  readonly powertrainIoMatrixDisplayName = POWERTRAIN_IO_MATRIX_DISPLAY_NAME;
  override permissionUrl = '/esciUI/icd/ioMatrixPermission';
  private readonly savedMatrixQuerySignal = signal('');
  private savedMatrixSelectionRequestId = 0;
  editExistingMode = false;
  savedIoMatrixOptions: { label: string; key: string; snapshot: IoMatrixSnapshot }[] = [];
  selectedSavedMatrixKey = '';
  selectedSavedMatrixLabel = '';
  newerSavedCadenceAvailable = false;
  addVariantDialogVisible = false;
  newVariantUsage = '';
  newVariantPartNumber = '';

  get addVariantDialogValid(): boolean {
    return !!(this.newVariantUsage || '').trim() && !!(this.newVariantPartNumber || '').trim();
  }

  get selectedView(): IoMatrixView {
    return this.selectedViewSignal();
  }

  set selectedView(value: IoMatrixView) {
    this.selectedViewSignal.set(value ?? 'architecture');
    this.applyIoMatrixViewVisibility();
  }

  protected override onIoMatrixColumnsRebuilt(): void {
    this.styleConnectorPinFilterRow();
  }

  openAddVariantDialog(): void {
    this.newVariantUsage = '';
    this.newVariantPartNumber = '';
    this.addVariantDialogVisible = true;
  }

  closeAddVariantDialog(): void {
    this.addVariantDialogVisible = false;
    this.newVariantUsage = '';
    this.newVariantPartNumber = '';
  }

  executeAddVariant(): void {
    const usage = (this.newVariantUsage || '').trim();
    const partNumber = (this.newVariantPartNumber || '').trim();
    if (!usage || !partNumber) {
      this.messageService.processError('Enter both Usage and Part Number.');
      return;
    }
    if (!variantTokenPartsAreValid(usage, partNumber)) {
      this.messageService.processError('Usage and Part Number must not contain the character |.');
      return;
    }
    const token = this.buildVariantToken(usage, partNumber);
    if (!token || token === '-') {
      this.messageService.processError('Enter valid Usage and Part Number.');
      return;
    }
    if (this.getActiveVariantTokens().includes(token)) {
      this.messageService.processError(`Variant ${token} already exists.`);
      return;
    }
    if (!this.sessionVariantTokens.includes(token)) {
      this.sessionVariantTokens = [...this.sessionVariantTokens, token];
    }
    for (const row of this.ioMatrixRows || []) {
      if ((row as any)?.state === 'deleted') {
        continue;
      }
      this.applyVariantCheckboxToggle(row, token, true);
    }
    this.rebuildIoMatrixColumns();
    if (this.isGridApiAlive(this.gridApi)) {
      this.gridApi.redrawRows();
    }
    this.closeAddVariantDialog();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.esciService.checkExportPermission(this);
    this.loadSavedIoMatrices();
  }

  toggleEditExistingMode(editExistingMode: boolean): void {
    this.editExistingMode = editExistingMode;
    if (!editExistingMode) {
      this.selectedSavedMatrixKey = '';
      this.selectedSavedMatrixLabel = '';
      this.savedMatrixQuerySignal.set('');
      this.newerSavedCadenceAvailable = false;
      // Returning to create-new mode must not inherit any state from the saved-matrix
      // selection. Delegate to `onObjectCodeCleared()` so the existing clear cascade
      // (Object Code, Object Code Inst, Architecture, Cadence, grid rows, template
      // rows, matrixInUse, cadence merge flags) runs through one place.
      this.onObjectCodeCleared();
      return;
    }
    this.loadSavedIoMatrices();
  }

  loadSavedIoMatrices(preferredSelectionKey?: string): void {
    this.esciService.getIoMatrixSnapshots().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: snapshots => {
        this.savedIoMatrixOptions = buildIoMatrixSnapshotOptions(snapshots);
        const selectionKey = preferredSelectionKey ?? this.selectedSavedMatrixKey;
        const selectedOption = selectionKey
          ? this.savedIoMatrixOptions.find(option => option.key === selectionKey)
          : undefined;
        if (selectedOption) {
          this.selectedSavedMatrixKey = selectionKey;
          this.selectedSavedMatrixLabel = selectedOption.label;
        } else if (preferredSelectionKey) {
          this.selectedSavedMatrixKey = '';
          this.selectedSavedMatrixLabel = '';
        }
      },
      error: () => {
        this.savedIoMatrixOptions = [];
        if (preferredSelectionKey) {
          this.selectedSavedMatrixKey = '';
          this.selectedSavedMatrixLabel = '';
        }
      }
    });
  }

  filterSavedIoMatrixSuggestions(event: { query?: string }): void {
    this.savedMatrixQuerySignal.set(event?.query ?? '');
  }

  onSavedIoMatrixSelected(): void {
    const selectedOption = this.savedIoMatrixOptions
      .find(option => option.label === this.selectedSavedMatrixLabel);
    this.selectedSavedMatrixKey = selectedOption?.key || '';
    this.onSavedIoMatrixChanged();
  }

  onSavedIoMatrixCleared(): void {
    this.selectedSavedMatrixLabel = '';
    this.selectedSavedMatrixKey = '';
    this.savedMatrixQuerySignal.set('');
    this.onSavedIoMatrixChanged();
  }

  get filteredSavedIoMatrixSuggestions(): string[] {
    const query = (this.savedMatrixQuerySignal() || '').trim().toLowerCase();
    const labels = this.savedIoMatrixOptions.map(option => option.label);
    if (!query) {
      return labels;
    }
    return labels.filter(label => (label || '').toLowerCase().includes(query));
  }

  onSavedIoMatrixChanged(): void {
    this.abandonIoMatrixMergePreviews();
    const requestId = ++this.savedMatrixSelectionRequestId;
    const selected = this.savedIoMatrixOptions.find(option => option.key === this.selectedSavedMatrixKey)?.snapshot;
    if (!selected) {
      this.newerSavedCadenceAvailable = false;
      return;
    }

    this.objectCode = selected.objectCode;
    this.objectCodeInst = selected.objectCodeInst;
    this.setObjectCodeInstSelected(true);
    this.architecture = selected.architecture;
    this.cadenceVer = selected.cadenceVer;
    this.deviceIoCadenceVer = '';
    const latestSavedCadence = this.pickLatestCadence(
      this.savedIoMatrixOptions
        .map(option => option.snapshot)
        .filter(snapshot => snapshot.matrixId === selected.matrixId)
        .map(snapshot => snapshot.cadenceVer || '')
    );
    const selectedIsLatestSaved = !latestSavedCadence || latestSavedCadence === selected.cadenceVer;
    this.latestSavedCadenceVer = latestSavedCadence || selected.cadenceVer;
    this.cadenceUpgradeAvailable = false;
    this.newerSavedCadenceAvailable = !selectedIsLatestSaved;
    this.matrixInUse = !!selected.inUse;
    this.outdatedCadence = false;
    this.refreshRefMapWarning();

    this.esciService.getIoMatrixSourceRows(selected.objectCode, selected.objectCodeInst, selected.architecture).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: sourceRows => {
        if (requestId !== this.savedMatrixSelectionRequestId) {
          return;
        }
        const nextSourceRows = sourceRows || [];
        const latestDeviceIoCadence = this.latestSourceCadenceFromRows(nextSourceRows);
        this.deviceIoCadenceVer = latestDeviceIoCadence;
        this.cadenceUpgradeAvailable = !!(selectedIsLatestSaved
          && latestDeviceIoCadence
          && selected.cadenceVer
          && latestDeviceIoCadence !== selected.cadenceVer);
      },
      error: () => {
        if (requestId !== this.savedMatrixSelectionRequestId) {
          return;
        }
        this.deviceIoCadenceVer = '';
        this.cadenceUpgradeAvailable = false;
      }
    });

    this.esciService.getIoMatrixRows(selected.matrixId, selected.cadenceVer).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: response => {
        if (requestId !== this.savedMatrixSelectionRequestId) {
          return;
        }
        const ioRows = response?.rows || [];
        this.applyIoMatrixLoadMetadata(response);
        this.setIoMatrixRows(ioRows);
        this.ioMatrixCreatedSignal.set(ioRows.length > 0);
      },
      error: () => {
        if (requestId !== this.savedMatrixSelectionRequestId) {
          return;
        }
        this.messageService.processError(`Failed to load saved ${POWERTRAIN_IO_MATRIX_DISPLAY_NAME} rows.`);
      }
    });
  }

  override saveIoMatrix(): void {
    super.saveIoMatrix();
  }

  protected override onIoMatrixSavedSuccessfully(): void {
    const selectionKey = this.editExistingMode ? `${this.matrixId()}|${this.cadenceVer || ''}` : undefined;
    this.loadSavedIoMatrices(selectionKey);
  }

}

